import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React, { useState } from 'react';

import { Panel } from '../../../../../components/atoms/Panel';
import { RichTextEditor } from '../../../../../components/atoms/RichTextEditor';
import { toast } from '../../../../../utils/toast';

export interface AddNotePanelProps {
  onAddNote?: (note: string, isInternal: boolean) => Promise<boolean> | void;
}

export const AddNotePanel: React.FC<AddNotePanelProps> = ({ onAddNote }) => {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmed = value.replace(/<(.|\n)*?>/g, '').trim();
    if (!trimmed || !onAddNote || submitting) return;
    setSubmitting(true);
    try {
      const result = await onAddNote(value, true);
      // Treat undefined return as success so legacy void callers keep working.
      // Only clear the editor on success — preserve the typed note on failure
      // so users can retry without retyping.
      if (result === false) {
        toast.error('Failed to save note. Please try again.');
      } else {
        setValue('');
        toast.success('Note added');
      }
    } catch {
      toast.error('Failed to save note. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Panel title="Add Note">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ '& .ql-editor': { minHeight: '160px' } }}>
          <RichTextEditor value={value} onValueChange={setValue} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="contained"
            disabled={submitting}
            onClick={handleSubmit}
            sx={{
              fontSize: '11px',
              textTransform: 'none',
              py: 0.75,
              px: 2.5,
              bgcolor: 'v4.accent',
              '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 },
            }}
          >
            Add Note
          </Button>
        </Box>
      </Box>
    </Panel>
  );
};
